import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  Close,
  LocationOnOutlined,
} from '@mui/icons-material';
import config from '../../config';
import AlertPreview from '../AlertPreview';
import { styles, targetingChipList } from '../../tasks/forms/TaskLaunchModal';

const { ALL_LANGUAGES } = config;

export default function AlertScheduleModal({
  alert,
  isOpen,
  isReachLoading,
  onClose,
  onConfirm,
  reach,
}) {
  const chips = targetingChipList(alert.targeting);

  function renderMetrics() {
    if (isReachLoading) {
      return (
        <Typography sx={{ ...styles.metricLabel, gridColumn: '1 / -1' }}>
          Calculating reach…
        </Typography>
      );
    }

    if (!reach) {
      return (
        <>
          <Box sx={styles.targetingMetric}>
            <Typography sx={styles.metricValue}>All</Typography>
            <Typography sx={styles.metricLabel}>stores</Typography>
          </Box>
          <Box sx={styles.targetingMetric}>
            <Typography sx={styles.metricValue}>All</Typography>
            <Typography sx={styles.metricLabel}>associates</Typography>
          </Box>
        </>
      );
    }

    return (
      <>
        <Box sx={styles.targetingMetric}>
          <Typography sx={styles.metricValue}>
            {reach.store_count.toLocaleString()}
            {reach.total_store_count != null && (
              <Box component="span" sx={styles.metricTotal}>
                of {reach.total_store_count.toLocaleString()}
              </Box>
            )}
          </Typography>
          <Typography sx={styles.metricLabel}>stores targeted</Typography>
        </Box>
        <Box sx={styles.targetingMetric}>
          <Typography sx={styles.metricValue}>
            {reach.user_count.toLocaleString()}
          </Typography>
          <Typography sx={styles.metricLabel}>associates targeted</Typography>
        </Box>
      </>
    );
  }

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isOpen}
      onClose={onClose}
      PaperProps={{ sx: styles.paper }}
      aria-labelledby="alert-schedule-title"
      aria-describedby="alert-schedule-description"
    >
      <Box sx={styles.header}>
        <Box sx={styles.statusIcon}>
          <CheckCircleOutline sx={styles.statusIconGlyph} />
        </Box>
        <Box>
          <Typography
            component="h2"
            id="alert-schedule-title"
            sx={styles.title}
          >
            Schedule alert
          </Typography>
          <Typography id="alert-schedule-description" sx={styles.subtitle}>
            Review your alert before posting it to stores.
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          size="small"
          sx={styles.closeButton}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={styles.content}>
        <Stack spacing={2.5}>
          {ALL_LANGUAGES.map((langKey) => (
            <Box key={langKey}>
              <Typography sx={styles.label}>{langKey}</Typography>
              <AlertPreview alert={alert} language={langKey} />
            </Box>
          ))}

          <Box>
            <Typography sx={styles.label}>Targeting</Typography>
            {chips.length > 0 ? (
              <Box sx={styles.targetingChips}>
                {chips.map((chip) => (
                  <Box key={chip.label} sx={styles.targetingChip}>
                    <LocationOnOutlined sx={styles.targetingIcon} />
                    {chip.label}
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ ...styles.value, mb: 1.5 }}>
                No filters — every store.
              </Typography>
            )}
            <Box sx={styles.targetingSummary}>{renderMetrics()}</Box>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={styles.actions}>
        <Button onClick={onClose} sx={styles.button} variant="outlined">
          Go back
        </Button>
        <Button
          onClick={onConfirm}
          sx={styles.confirmButton}
          variant="contained"
        >
          Set alert live
        </Button>
      </DialogActions>
    </Dialog>
  );
}

AlertScheduleModal.propTypes = {
  alert: PropTypes.shape({
    languages: PropTypes.shape({}),
    targeting: PropTypes.shape({}),
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  isReachLoading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  reach: PropTypes.shape({
    store_count: PropTypes.number,
    user_count: PropTypes.number,
    total_store_count: PropTypes.number,
  }),
};

AlertScheduleModal.defaultProps = {
  isReachLoading: false,
  reach: null,
};
